import React from 'react';
import Head from 'next/head';

interface StructuredDataProps {
  data: Record<string, any>;
}

/**
 * Serialize JSON-LD safely for embedding inside a <script> tag.
 *
 * JSON.stringify does not escape "<", so content-derived strings (e.g. WordPress
 * post text containing "</script>" or "<!--") could terminate the script block
 * and inject markup into the page. Escaping "<" as the \u003c JSON escape parses
 * identically, making the payload inert. U+2028/U+2029 are escaped for the same
 * reason (legal in JSON, illegal in JS source).
 */
export const safeJsonLd = (data: Record<string, any>): string =>
  JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

/**
 * Generic component for adding structured data (JSON-LD) to a page
 */
export const StructuredData = ({ data }: StructuredDataProps) => {
  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
      />
    </Head>
  );
};

/**
 * Component for adding Organization structured data
 */
export const OrganizationSchema = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GearFocus',
    url: 'https://gearfocus.com',
    logo: 'https://gearfocus.com/logo.svg',
    sameAs: [
      'https://www.facebook.com/gearfocus',
      'https://www.instagram.com/gearfocus',
      'https://twitter.com/gearfocus',
      'https://www.youtube.com/gearfocus'
    ],
    description: 'The trusted marketplace for buying and selling used camera and video equipment. Built by creators, for creators.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'support@gearfocus.com',
      areaServed: ['US', 'CA']
    }
  };

  return <StructuredData data={data} />;
};

/**
 * Component for adding WebSite structured data
 */
export const WebsiteSchema = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'GearFocus',
    url: 'https://gearfocus.com',
    potentialAction: {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': 'https://gearfocus.com/search?q={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return <StructuredData data={data} />;
};

/**
 * Component for adding Product structured data
 */
interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: number;
  priceCurrency?: string;
  sku: string;
  brand: string;
  condition?: 'NewCondition' | 'UsedCondition' | 'RefurbishedCondition';
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  url: string;
  /** Manufacturer identifiers for the underlying model (used gear is one-of-a-kind,
   *  so these describe the model, not the individual item). Omit entirely when
   *  GearMaster doesn't have the value — never emit an empty string. */
  gtin?: string | null;
  mpn?: string | null;
  /** Store sale end is an exclusive instant; omit for ordinary listings. */
  saleEndsAtMs?: number;
}

export function salePriceValidUntil(exclusiveEndMs: number): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(exclusiveEndMs - 1));
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export const ProductSchema = ({
  name,
  description,
  image,
  price,
  priceCurrency = 'USD',
  sku,
  brand,
  condition = 'UsedCondition',
  availability = 'InStock',
  url,
  gtin,
  mpn,
  saleEndsAtMs,
}: ProductSchemaProps) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image,
    sku,
    ...(gtin && { gtin }),
    ...(mpn && { mpn }),
    brand: {
      '@type': 'Brand',
      name: brand
    },
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency,
      ...(saleEndsAtMs != null && Number.isFinite(saleEndsAtMs)
        ? { priceValidUntil: salePriceValidUntil(saleEndsAtMs) }
        : {}),
      availability: `https://schema.org/${availability}`,
      itemCondition: `https://schema.org/${condition}`,
      url
    }
  };

  return <StructuredData data={data} />;
};

/**
 * Component for adding Article structured data
 */
interface ArticleSchemaProps {
  headline: string;
  description: string;
  image: string;
  author: string;
  publisher: string;
  publisherLogo: string;
  datePublished: string;
  dateModified?: string;
  url: string;
}

export const ArticleSchema = ({
  headline,
  description,
  image,
  author,
  publisher,
  publisherLogo,
  datePublished,
  dateModified,
  url
}: ArticleSchemaProps) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image,
    author: {
      '@type': 'Person',
      name: author
    },
    publisher: {
      '@type': 'Organization',
      name: publisher,
      logo: {
        '@type': 'ImageObject',
        url: publisherLogo
      }
    },
    datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    }
  };

  return <StructuredData data={data} />;
};

/**
 * Component for adding BlogPosting structured data
 *
 * Mirrors ArticleSchema but emits @type BlogPosting. The caller passes authorType
 * ('Person' for a named human byline, 'Organization' for brand/staff); the shared
 * resolveBlogAuthor helper in src/utils/blog/author.ts decides which. datePublished
 * is optional: if the source date can't be normalized we omit both date fields
 * rather than emit an invalid value.
 */
interface BlogPostingSchemaProps {
  headline: string;
  description: string;
  image: string;
  authorName: string;
  authorType?: 'Organization' | 'Person';
  publisher: string;
  publisherLogo: string;
  datePublished?: string;
  dateModified?: string;
  url: string;
}

export const BlogPostingSchema = ({
  headline,
  description,
  image,
  authorName,
  authorType = 'Organization',
  publisher,
  publisherLogo,
  datePublished,
  dateModified,
  url
}: BlogPostingSchemaProps) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline,
    description,
    image,
    author: {
      '@type': authorType,
      name: authorName
    },
    publisher: {
      '@type': 'Organization',
      name: publisher,
      logo: {
        '@type': 'ImageObject',
        url: publisherLogo
      }
    },
    ...(datePublished && { datePublished }),
    // Gated on either date, not on datePublished alone. resolveDateModified is
    // specified (and tested) to return a known modified date even when the
    // publish date is unknown; gating this on datePublished meant that contract
    // was asserted by a green test and honoured by no call site. schema.org
    // permits dateModified without datePublished, and a known date is better
    // than silence.
    ...((dateModified || datePublished) && {
      dateModified: dateModified || datePublished
    }),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    }
  };

  return <StructuredData data={data} />;
};

/**
 * Component for adding BreadcrumbList structured data
 */
interface BreadcrumbSchemaProps {
  items: {
    name: string;
    url: string;
  }[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return <StructuredData data={data} />;
};

/**
 * Component for adding FAQPage structured data
 */
interface FAQSchemaProps {
  faqs: {
    question: string;
    answer: string;
  }[];
}

export const FAQSchema = ({ faqs }: FAQSchemaProps) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return <StructuredData data={data} />;
};

/**
 * A single step in a HowTo. `name` is the short step heading, `text` the
 * instruction body. Both are required — a step missing either is not a usable
 * instruction and is dropped rather than emitted as a half-filled node.
 */
export interface HowToStepInput {
  name: string;
  text: string;
  url?: string;
  image?: string;
}

interface HowToSchemaProps {
  name: string;
  steps: HowToStepInput[];
  description?: string;
  /** ISO 8601 duration, e.g. "PT5M" for five minutes. */
  totalTime?: string;
  image?: string;
  supply?: string[];
  tool?: string[];
}

/**
 * Component for adding HowTo structured data.
 *
 * GATING IS THE POINT: this renders nothing at all unless the caller passes at
 * least one step with both a name and a text. That makes it safe to wire into a
 * template ahead of the content pipeline that will feed it — until a post
 * actually carries steps, the rendered output is byte-identical to today's.
 *
 * Honest note on value: Google retired HowTo *rich results* from Search in 2023,
 * so this does not produce a visual rich result there. It is still valid
 * schema.org markup and is read by AI answer engines, which is the reason it
 * exists here. Do not expect a Search appearance change from it.
 */
export const HowToSchema = ({
  name,
  steps,
  description,
  totalTime,
  image,
  supply,
  tool
}: HowToSchemaProps) => {
  // Drop unusable steps before deciding whether there is anything to emit, so a
  // caller passing [{name:'', text:''}] gets silence rather than an empty HowTo.
  //
  // The typeof checks are not redundant with the TypeScript types. This component
  // is built to be fed by the blog content pipeline, whose output is untyped at
  // runtime; `step.name?.trim()` would guard null but still throw on a number,
  // and a throw here takes down the whole article page. Anything that is not a
  // non-empty string is simply not a usable instruction, so it is dropped.
  const isFilled = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

  const validSteps = (Array.isArray(steps) ? steps : []).filter(
    (step) => step && isFilled(step.name) && isFilled(step.text)
  );

  if (!isFilled(name) || validSteps.length === 0) return null;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    // Every one of these uses the same runtime validation as the steps above, and
    // for the same reason. `supply?.length` is truthy for the string 'tripod'
    // (length 6), and `supply.map` is then undefined — a TypeError thrown here
    // during SSR takes down the whole article page. Array.isArray is the check
    // that actually holds; the optional-chained .length is the one that looks
    // like it does. The string fields are validated too, so a non-string cannot
    // reach JSON.stringify.
    ...(isFilled(description) && { description }),
    ...(isFilled(totalTime) && { totalTime }),
    ...(isFilled(image) && { image }),
    ...(Array.isArray(supply) &&
      supply.filter(isFilled).length > 0 && {
        supply: supply
          .filter(isFilled)
          .map((item) => ({ '@type': 'HowToSupply', name: item }))
      }),
    ...(Array.isArray(tool) &&
      tool.filter(isFilled).length > 0 && {
        tool: tool.filter(isFilled).map((item) => ({ '@type': 'HowToTool', name: item }))
      }),
    step: validSteps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      // isFilled, not truthiness: these two were the last props spread raw. A
      // circular object or a hostile toJSON here throws inside JSON.stringify,
      // which 500s the article page. Same guard as every other field.
      ...(isFilled(step.url) && { url: step.url }),
      ...(isFilled(step.image) && { image: step.image })
    }))
  };

  return <StructuredData data={data} />;
};

/**
 * Component for adding VideoObject structured data
 */
interface VideoObjectSchemaProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
  uploadDate: string;
  duration?: string;
}

export const VideoObjectSchema = ({
  name,
  description,
  thumbnailUrl,
  contentUrl,
  uploadDate,
  duration
}: VideoObjectSchemaProps) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl,
    contentUrl,
    uploadDate,
    ...(duration && { duration })
  };

  return <StructuredData data={data} />;
};
